import * as React from 'react';

import { FormAction } from 'src/ui/components/FormActions';
import { AnyFunc } from 'src/ui/constants/types';
import { ConnectionForm } from 'src/ui/containers/dialogs/ConnectionForm';
import { makeSubmitAction } from 'src/ui/containers/dialogs/makeSubmitAction';
import { useInvalidateConnections } from 'src/ui/containers/endpoints/utils/invalidate';
import { Connection, disconnected } from 'src/ui/features/connection';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { AddConnectionMutation } from 'src/ui/graph/sdk';
import {
  useAddConnectionMutation,
  useSavedConnectionsQuery,
  useUserPartnerConnectionsQuery,
} from 'src/ui/graph/types';
import {
  useClearConnection,
  useNavigateToConnection,
  useNavigateToPartnerConnection,
  useVerifyConnection,
} from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import * as copy from 'src/ui/templates/copy';
import { doServiceRedirect } from 'src/ui/utils/window';

interface NewConnectionFormProps {
  connection?: Connection;
  redirectSubPath?: string;
  isLaunchpad?: boolean;
}

export const NewConnectionForm: React.VFC<NewConnectionFormProps> = ({
  connection,
  redirectSubPath,
  isLaunchpad = false,
}) => {
  const { data: partnerInfo } = useUserPartnerConnectionsQuery(graphQLClient);
  const clearConnection = useClearConnection();
  const onError = useOnError();
  const onMutate = useOnMutate();
  const navigateToConnection = useNavigateToConnection();
  const navigateToPartnerConnection = useNavigateToPartnerConnection();
  const invalidateConnections = useInvalidateConnections('');
  const { data, isLoading } = useSavedConnectionsQuery(graphQLClient, {});
  const {
    isLoading: isValidating,
    mutate: validate,
    reset: resetValidation,
  } = useVerifyConnection();

  React.useEffect(() => () => resetValidation(), [resetValidation]);

  const newConnection = connection || disconnected;

  const {
    isLoading: isAddingConnection,
    mutate: addConnection,
  } = useAddConnectionMutation(graphQLClient, {
    onError,
    onMutate,
    onSuccess: (c: AddConnectionMutation) => {
      if (!c.addConnection) {
        clearConnection();
        return;
      }

      invalidateConnections();

      if (redirectSubPath) {
        doServiceRedirect(c.addConnection.index, redirectSubPath);
        return;
      }

      if (partnerInfo?.userPartnerConnections?.length) {
        navigateToPartnerConnection(`/databricks-partner-home`);
        return;
      }

      navigateToConnection(c.addConnection.index);
    },
  });

  const [savedNames, setSavedNames] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (!data || !data.listConnections) {
      return;
    }
    setSavedNames(data.listConnections.map((conn) => conn?.name || 'unknown'));
  }, [data]);

  const handleConnect = (conn: Connection, reset: AnyFunc) => {
    validate(conn, {
      onError,
      onSuccess: (data) => {
        addConnection({ input: data }, { onSuccess: reset });
      },
    });
  };

  const isWorking = isLoading || isAddingConnection || isValidating;
  const submitDisabled = (conn: Connection) =>
    isWorking || submitDisabledText(conn) !== '';

  const submitDisabledText = (conn: Connection): string => {
    if (!conn.useSSO && conn.password && !conn.username) {
      return copy.components.connectionDialog.formValidation.usernameRequired;
    }

    if (!conn.useSSO && conn.username && !conn.password) {
      return copy.components.connectionDialog.formValidation.passwordRequired;
    }

    if (!conn.endpoint) {
      return copy.components.connectionDialog.formValidation.endpointRequired;
    }

    if (conn.endpoint && !conn.endpoint.startsWith('http')) {
      return copy.components.connectionDialog.formValidation.invalidEndpoint;
    }

    if (savedNames.includes(conn.name)) {
      return copy.components.connectionDialog.formValidation.nameUniq;
    }

    if (!conn.name) {
      return copy.components.connectionDialog.formValidation.nameRequired;
    }

    return '';
  };

  const actions: FormAction[] = [
    makeSubmitAction(copy.components.connectionDialog.new.submit, isWorking),
  ];

  return (
    <ConnectionForm
      actions={actions}
      connection={newConnection}
      onSubmit={handleConnect}
      submitDisabled={submitDisabled}
      submitTooltip={submitDisabledText}
      isLaunchpad={isLaunchpad}
    />
  );
};
